import { useMemo, useState } from 'react';

import { STRINGS } from '@/constants/strings';

import { PlayerLastActiveFilter, usePlayerFilters } from '../context/player-filters-context';
import { PlayerSortKey, PlayerSortOrder, usePlayerSort } from '../context/player-sort-context';

export function useFilterPlayer() {
  const [open, setOpen] = useState(false);
  const { level, lastActive, setFilters } = usePlayerFilters();

  const [draftLevel, setDraftLevel] = useState<number | null>(level);
  const [draftLastActive, setDraftLastActive] = useState<PlayerLastActiveFilter | null>(lastActive);

  const [sortOpen, setSortOpen] = useState(false);
  const { key: sortKey, order: sortOrder, setSort } = usePlayerSort();
  const [sortDraft, setSortDraft] = useState<{ key: PlayerSortKey; order: PlayerSortOrder }>({
    key: sortKey,
    order: sortOrder,
  });

  const filterButtonLabel = useMemo(() => {
    return STRINGS.player.filters.buttonDefault;
  }, []);

  const sortButtonLabel = useMemo(() => {
    return STRINGS.player.sort.buttonDefault;
  }, []);

  const closeWithoutSave = () => {
    setOpen(false);
  };

  const saveAndClose = () => {
    setFilters({ level: draftLevel, lastActive: draftLastActive ?? 'show_all' });
    setOpen(false);
  };

  const closeSortWithoutSave = () => {
    setSortOpen(false);
  };

  const saveSortAndClose = () => {
    // If a level filter is selected, force non-level sort.
    const nextKey: PlayerSortKey =
      level != null && sortDraft.key === 'level' ? 'name' : sortDraft.key;
    setSort({ key: nextKey, order: sortDraft.order });
    setSortOpen(false);
  };

  const forceSortToNameAsc = () => {
    setSort({ key: 'name', order: 'asc' });
    setSortDraft({ key: 'name', order: 'asc' });
  };

  const LEVELS = useMemo(() => Array.from({ length: 10 }, (_, i) => i + 1), []);

  const lastActiveOptions: Array<{ key: PlayerLastActiveFilter; label: string }> = [
    { key: '1_month', label: STRINGS.player.filters.lastActive.oneMonth },
    { key: '3_months', label: STRINGS.player.filters.lastActive.threeMonths },
    { key: '6_months', label: STRINGS.player.filters.lastActive.sixMonths },
    { key: '1_5_years', label: STRINGS.player.filters.lastActive.oneHalfYears },
    { key: 'show_all', label: STRINGS.player.filters.lastActive.showAll },
  ];

  return {
    open,
    setOpen,
    level,
    lastActive,
    setFilters,
    draftLevel,
    setDraftLevel,
    draftLastActive,
    setDraftLastActive,
    filterButtonLabel,
    closeWithoutSave,
    saveAndClose,
    LEVELS,
    lastActiveOptions,

    sortOpen,
    setSortOpen,
    sortKey,
    sortOrder,
    sortDraft,
    setSortDraft,
    sortButtonLabel,
    closeSortWithoutSave,
    saveSortAndClose,
    forceSortToNameAsc,
  };
}
